import { InputEvents, Metrics, ObjectState, Surface, Obj } from "../types";
import { CreateObjectArgs, createSprite } from "../core";
import { scaleLinear } from "d3-scale";
import { FixedSizeLifoQueue } from "../queue";
import { xaosPresets } from "../xaosPresets";
import { CubeViewController } from "../cubeView";

type CreateXaosArgs = CreateObjectArgs & {
  w: number;
  h: number;
}

const presets = [
  [34, 21, -55],
  [34, 3, -89],
  [21, 5, -34],
  [-2, 21, 8],
  [-18, 162, -18],
  [-1, 100, -2]
];

const dfq = (x, y, z, a, b, c, d) => {
  const xDot =
    (a - c + z * y - (a - 1) * x) / d +
    (2 * (a + c - z) * x * y + (1 - a) * (x ** 2 - y ** 2)) /
    (d * Math.sqrt(x ** 2 + y ** 2));
  const yDot =
    ((c - a - z) * x - (a + 1) * y) / d +
    (2 * (a - 1) * x * y + (a + c - z) * (x ** 2 - y ** 2)) /
    (d * Math.sqrt(x ** 2 + y ** 2));
  const zDot = (d * x ** 2 * y - y ** 3) / 2 - b * z;
  return [xDot, yDot, zDot];
};


export const createXaos = (args: CreateXaosArgs) => {
  const cubeViewController = new CubeViewController();
  cubeViewController.rotate3DDown();

  const width = args.w;
  const height = args.h;

  const minDimension = Math.min(width, height);
  const displayXMin = 0 + Math.floor((width - minDimension) / 2);
  const displayXMax = width - Math.floor((width - minDimension) / 2);
  const displayYMin = 0 + Math.floor((height - minDimension) / 2);
  const displayYMax = height - Math.floor((height - minDimension) / 2);

  const sprite = createSprite({ w: width, h: height, fill: 0 });

  let nSteps = 10;
  let nStepsPrev = nSteps;

  const n = sprite.c.length * 3;

  const xs = new FixedSizeLifoQueue(n);
  const ys = new FixedSizeLifoQueue(n);
  const zs = new FixedSizeLifoQueue(n);

  let xInit = -8;
  let yInit = 4;
  let zInit = 10;

  xs.push(xInit);
  ys.push(yInit);
  zs.push(zInit);

  const dt = 0.001;

  let d = 3;

  // let [a, b, c] = presets[0]; // 0, 3, 5

  let presetIdx = 56;
  const { alpha, beta, gamma } = xaosPresets[presetIdx]; // 3, 4005, 46
  let a = alpha;
  let b = beta;
  let c = gamma;


  // let [a, b, c] = [-3, 144, 3];

  const restart = () => {
    xs.clear();
    ys.clear();
    zs.clear();
    xs.push(xInit);
    ys.push(yInit);
    zs.push(zInit);
  };

  // TODO why does pause twice freeze?
  const pause = () => {
    nStepsPrev = nSteps;
    nSteps = 0;
  };

  const play = () => {
    nSteps = nStepsPrev;
  };

  const presetDec = () => {
    presetIdx--;
    if (presetIdx < 0) {
      presetIdx = xaosPresets.length - 1;
    }

    const { alpha, beta, gamma } = xaosPresets[presetIdx];
    a = alpha;
    b = beta;
    c = gamma;
    restart();
  }

  const presetInc = () => {
    presetIdx++;
    if (presetIdx >= xaosPresets.length) {
      presetIdx = 0;
    }

    const { alpha, beta, gamma } = xaosPresets[presetIdx];
    a = alpha;
    b = beta;
    c = gamma;
    restart();
  }

  const getPresetIdx = () => {
    return presetIdx;
  }

  const rotate2DLeft = () => {
    cubeViewController.rotate2DLeft();
  }

  const rotate2DRight = () => {
    cubeViewController.rotate2DRight();
  }

  const rotate3DLeft = () => {
    cubeViewController.rotate3DLeft();
  }

  const rotate3DRight = () => {
    cubeViewController.rotate3DRight();
  }

  const rotate3DUp = () => {
    cubeViewController.rotate3DUp();
  }

  const rotate3DDown = () => {
    cubeViewController.rotate3DDown();
  }

  const getOrientationState = () => {
    const state = cubeViewController.getState();
    return `${state.upOnScreen}${state.rightOnScreen}`;
  }

  const stepDfq = () => {
    const x1 = xs.peek();
    const y1 = ys.peek();
    const z1 = zs.peek();

    // calc next element
    const [xDot, yDot, zDot] = dfq(
      x1,
      y1,
      z1,
      a,
      b,
      c,
      d,
    );

    // Take a step
    const x2 = x1 + xDot * dt;
    const y2 = y1 + yDot * dt;
    const z2 = z1 + zDot * dt;

    xs.push(x2);
    ys.push(y2);
    zs.push(z2);
  }

  const step = (startTime: number, timestamp: number, inputEvents: InputEvents, metrics: Metrics, inFocus: boolean, objectState: ObjectState, surface: Surface, obj: Obj) => {
    // const dt = (timestamp - startTime) / 1000; // time in seconds

    // Take nSteps dfq steps for every render step
    // TODO utilize time
    for (let i = 0; i < nSteps; i++) {
      stepDfq();
    }

    // reset sprite
    obj.sprite = createSprite({ w: width, h: height, fill: 0 });

    // layout for image

    const { upOnScreen, rightOnScreen } = cubeViewController.getState();
    const upOnScreenSign = upOnScreen.charAt(0);
    const upOnScreenAxis = upOnScreen.charAt(1);
    const rightOnScreenSign = rightOnScreen.charAt(0);
    const rightOnScreenAxis = rightOnScreen.charAt(1);

    // handle perspective to image layout

    // map perspective axes to image axes

    let xMin = 0;
    let xMax = 0;
    let yMin = 0;
    let yMax = 0;
    let zMin = 0;
    let zMax = 0;

    let upOnScreenScale;
    let rightOnScreenScale;

    const upOnScreenRange = upOnScreenSign === "+" ? [displayYMin, displayYMax] : [displayYMax, displayYMin];
    const rightOnScreenRange = rightOnScreenSign === "+" ? [displayXMin, displayXMax] : [displayXMax, displayXMin];

    if (upOnScreenAxis === "X" || rightOnScreenAxis === "X") {
      const xsf = xs.store.filter(v => v !== undefined);
      xMin = Math.min(...xsf);
      xMax = Math.max(...xsf);
      if (upOnScreenAxis === "X") {
        upOnScreenScale = scaleLinear().domain([xMin, xMax]).range(upOnScreenRange);
      } else {
        rightOnScreenScale = scaleLinear().domain([xMin, xMax]).range(rightOnScreenRange);
      }
    }
    if (upOnScreenAxis === "Y" || rightOnScreenAxis === "Y") {
      const ysf = ys.store.filter(v => v !== undefined);
      yMin = Math.min(...ysf);
      yMax = Math.max(...ysf);
      if (upOnScreenAxis === "Y") {
        upOnScreenScale = scaleLinear().domain([yMin, yMax]).range(upOnScreenRange);
      } else {
        rightOnScreenScale = scaleLinear().domain([yMin, yMax]).range(rightOnScreenRange);
      }
    }
    if (upOnScreenAxis === "Z" || rightOnScreenAxis === "Z") {
      const zsf = zs.store.filter(v => v !== undefined);
      zMin = Math.min(...zsf);
      zMax = Math.max(...zsf);
      if (upOnScreenAxis === "Z") {
        upOnScreenScale = scaleLinear().domain([zMin, zMax]).range(upOnScreenRange);
      } else {
        rightOnScreenScale = scaleLinear().domain([zMin, zMax]).range(rightOnScreenRange);
      }

    }

    xs.store.forEach((x, i) => {

      // select the y,z plane
      const y = ys.store[i];
      const z = zs.store[i];

      if (isNaN(x) || isNaN(y) || isNaN(z)) {
        return;
      }

      let rightOnScreenDomainValue;
      let upOnScreenDomainValue;

      if (upOnScreenAxis === "X" || rightOnScreenAxis === "X") {
        if (upOnScreenAxis === "X") {
          upOnScreenDomainValue = x;
        } else {
          rightOnScreenDomainValue = x;
        }
      }
      if (upOnScreenAxis === "Y" || rightOnScreenAxis === "Y") {
        if (upOnScreenAxis === "Y") {
          upOnScreenDomainValue = y;
        } else {
          rightOnScreenDomainValue = y;
        }
      }
      if (upOnScreenAxis === "Z" || rightOnScreenAxis === "Z") {
        if (upOnScreenAxis === "Z") {
          upOnScreenDomainValue = z;
        } else {
          rightOnScreenDomainValue = z;
        }
      }

      // map perspective to image plane
      const displayX = Math.floor(rightOnScreenScale(rightOnScreenDomainValue));
      const displayY = Math.floor(-1 * upOnScreenScale(upOnScreenDomainValue) + height);
      // TODO centralize this logic in a method on the sprite, i.e., setPixel(x,y,value)
      const idx = displayY * width + displayX;
      obj.sprite.c[idx] = 1;
    });
  }

  const xaos = {
    ...args,
    sprite,
    step,
    restart,
    pause,
    play,
    presetDec,
    presetInc,
    getPresetIdx,
    rotate2DLeft,
    rotate2DRight,
    rotate3DLeft,
    rotate3DRight,
    rotate3DUp,
    rotate3DDown,
    getOrientationState,
  }

  return xaos;
}
